/**
 * CSV Helper Functions
 * CSV処理用ユーティリティ関数
 */

import Papa from 'papaparse';

// UTF-8 BOMを追加（Excelでの文字化け防止）
const addBOM = (csv: string): string => {
  return '\uFEFF' + csv;
};

// CSVダウンロード関数
export const downloadCSV = (data: unknown[], filename: string) => {
  const csv = Papa.unparse(data, {
    header: true,
    columns: Object.keys((data[0] as Record<string, unknown>) || {}),
  });

  const blob = new Blob([addBOM(csv)], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

// 通常メニューCSVフォーマット
export interface NormalMenuCSV {
  ID: string;
  メニュー名: string;
  説明: string;
  カテゴリ: string;
  検索用カテゴリ: string;
  価格: number;
  価格表示: string;
  '所要時間(分)': number;
  掲載: string;
  予約可: string;
  有効: string;
  削除?: string;
}

// クーポンメニューCSVフォーマット
export interface CouponMenuCSV {
  ID: string;
  種別: string;
  クーポン名: string;
  内容: string;
  カテゴリ: string;
  検索用カテゴリ: string;
  価格: number;
  '所要時間(分)': number;
  提示条件: string;
  利用条件: string;
  有効期限: string;
  自動更新: string;
  メニュー指定: string;
  掲載: string;
  有効: string;
  削除?: string;
}

// カテゴリIDと名前のマッピング
const categoryMap: Record<string, string> = {
  'relax': 'リラクゼーション',
  'body': 'ボディケア・マッサージ',
  'head': 'ヘッドスパ',
  'esthe': 'エステ',
  'other': 'その他'
};

const reverseCategoryMap: Record<string, string> = Object.entries(categoryMap).reduce(
  (acc, [key, value]) => ({ ...acc, [value]: key }),
  {}
);

// Service型からNormalMenuCSVへの変換
export const serviceToNormalCSV = (service: unknown): NormalMenuCSV => {
  const serviceObj = service as Record<string, unknown>;
  return {
    ID: serviceObj.id as string,
    メニュー名: serviceObj.name as string,
    説明: (serviceObj.description as string) || '',
    カテゴリ: categoryMap[serviceObj.categoryId as string] || (serviceObj.categoryId as string),
    検索用カテゴリ: categoryMap[(serviceObj.searchCategoryId as string) || (serviceObj.categoryId as string)] || (serviceObj.searchCategoryId as string) || (serviceObj.categoryId as string),
    価格: serviceObj.price as number,
    価格表示: serviceObj.priceDisplay === 'range' ? '〜表示' :
             serviceObj.priceDisplay === 'ask' ? '要問い合わせ' : '通常',
    '所要時間(分)': serviceObj.duration as number,
    掲載: serviceObj.isPublished ? '○' : '×',
    予約可: serviceObj.isReservable ? '○' : '×',
    有効: serviceObj.isActive ? '○' : '×',
  };
};

// Service型からCouponCSVへの変換
export const serviceToCouponCSV = (service: unknown): CouponMenuCSV => {
  const serviceObj = service as Record<string, unknown>;
  return {
    ID: serviceObj.id as string,
    種別: serviceObj.couponType === 'new' ? '新規' :
          serviceObj.couponType === 'repeat' ? '再来' : '全員',
    クーポン名: serviceObj.name as string,
    内容: (serviceObj.description as string) || '',
    カテゴリ: categoryMap[serviceObj.categoryId as string] || (serviceObj.categoryId as string),
    検索用カテゴリ: categoryMap[(serviceObj.searchCategoryId as string) || (serviceObj.categoryId as string)] || (serviceObj.searchCategoryId as string) || (serviceObj.categoryId as string),
    価格: serviceObj.price as number,
    '所要時間(分)': serviceObj.duration as number,
    提示条件: (serviceObj.presentCondition as string) || '予約時',
    利用条件: (serviceObj.useCondition as string) || 'どなたでもご利用可',
    有効期限: (serviceObj.validUntil as string) || '',
    自動更新: serviceObj.autoRenewal ? '○' : '×',
    メニュー指定: serviceObj.menuDesignation === 'without' ? 'なし' : 'あり',
    掲載: serviceObj.isPublished !== false ? '○' : '×',
    有効: serviceObj.isActive ? '○' : '×',
  };
};

// NormalMenuCSVからService型への変換
export const normalCSVToService = (csv: NormalMenuCSV): unknown => {
  return {
    id: csv.ID || '',
    type: 'normal' as const,
    name: csv.メニュー名,
    description: csv.説明,
    categoryId: reverseCategoryMap[csv.カテゴリ] || 'other',
    searchCategoryId: reverseCategoryMap[csv.検索用カテゴリ] || reverseCategoryMap[csv.カテゴリ] || 'other',
    price: Number(csv.価格) || 0,
    priceDisplay: csv.価格表示 === '〜表示' ? 'range' :
                  csv.価格表示 === '要問い合わせ' ? 'ask' : 'normal',
    duration: Number(csv['所要時間(分)']) || 60,
    isPublished: csv.掲載 === '○',
    isReservable: csv.予約可 === '○',
    isActive: csv.有効 === '○',
  };
};

// CouponMenuCSVからService型への変換
export const couponCSVToService = (csv: CouponMenuCSV): unknown => {
  return {
    id: csv.ID || '',
    type: 'coupon' as const,
    name: csv.クーポン名,
    description: csv.内容,
    categoryId: reverseCategoryMap[csv.カテゴリ] || 'other',
    searchCategoryId: reverseCategoryMap[csv.検索用カテゴリ] || reverseCategoryMap[csv.カテゴリ] || 'other',
    price: Number(csv.価格) || 0,
    duration: Number(csv['所要時間(分)']) || 60,
    couponType: csv.種別 === '新規' ? 'new' :
                csv.種別 === '再来' ? 'repeat' : 'all',
    presentCondition: csv.提示条件 || '予約時',
    useCondition: csv.利用条件 || 'どなたでもご利用可',
    validUntil: csv.有効期限 || '',
    autoRenewal: csv.自動更新 === '○',
    menuDesignation: csv.メニュー指定 === 'なし' ? 'without' : 'with',
    isPublished: csv.掲載 === '○',
    isActive: csv.有効 === '○',
  };
};

// バリデーション関数
export const validateNormalCSV = (csv: NormalMenuCSV): string[] => {
  const errors: string[] = [];

  if (!csv.メニュー名) {
    errors.push('メニュー名は必須です');
  } else if (csv.メニュー名.length > 40) {
    errors.push('メニュー名は40文字以内で入力してください');
  }

  if (csv.説明 && csv.説明.length > 70) {
    errors.push('説明は70文字以内で入力してください');
  }

  if (!csv.価格 || isNaN(Number(csv.価格))) {
    errors.push('価格は数値で入力してください');
  }

  if (!csv['所要時間(分)'] || isNaN(Number(csv['所要時間(分)']))) {
    errors.push('所要時間は数値（分）で入力してください');
  }

  const validCategories = Object.values(categoryMap);
  if (!validCategories.includes(csv.カテゴリ)) {
    errors.push(`カテゴリは ${validCategories.join('、')} から選択してください`);
  }

  return errors;
};

export const validateCouponCSV = (csv: CouponMenuCSV): string[] => {
  const errors: string[] = [];

  if (!csv.クーポン名) {
    errors.push('クーポン名は必須です');
  } else if (csv.クーポン名.length > 40) {
    errors.push('クーポン名は40文字以内で入力してください');
  }

  if (csv.内容 && csv.内容.length > 90) {
    errors.push('内容は90文字以内で入力してください');
  }

  if (!csv.価格 || isNaN(Number(csv.価格))) {
    errors.push('価格は数値で入力してください');
  }

  if (!csv['所要時間(分)'] || isNaN(Number(csv['所要時間(分)']))) {
    errors.push('所要時間は数値（分）で入力してください');
  }

  const validCategories = Object.values(categoryMap);
  if (!validCategories.includes(csv.カテゴリ)) {
    errors.push(`カテゴリは ${validCategories.join('、')} から選択してください`);
  }

  const validTypes = ['全員', '新規', '再来'];
  if (!validTypes.includes(csv.種別)) {
    errors.push('種別は 全員、新規、再来 から選択してください');
  }

  if (csv.利用条件 && csv.利用条件.length > 20) {
    errors.push('利用条件は20文字以内で入力してください');
  }

  return errors;
};

// テンプレート生成
export const getNormalMenuTemplate = (): NormalMenuCSV[] => {
  return [
    {
      ID: '',
      メニュー名: '【入力ルール】40文字以内で入力',
      説明: '【入力ルール】70文字以内で入力。メニューの詳細説明を記載',
      カテゴリ: 'リラクゼーション',
      検索用カテゴリ: 'リラクゼーション',
      価格: 0,
      価格表示: '通常',
      '所要時間(分)': 0,
      掲載: '○',
      予約可: '○',
      有効: '○',
    },
    {
      ID: 'normal_1',
      メニュー名: 'ドライヘッドスパ 60分',
      説明: '頭皮の緊張をほぐし、深いリラクゼーションへ導きます',
      カテゴリ: 'ヘッドスパ',
      検索用カテゴリ: 'ヘッドスパ',
      価格: 6600,
      価格表示: '通常',
      '所要時間(分)': 60,
      掲載: '○',
      予約可: '○',
      有効: '○',
    },
    {
      ID: 'normal_2',
      メニュー名: '全身アロマトリートメント 90分',
      説明: '厳選したアロマオイルで全身をトリートメント',
      カテゴリ: 'ボディケア・マッサージ',
      検索用カテゴリ: 'ボディケア・マッサージ',
      価格: 9900,
      価格表示: '通常',
      '所要時間(分)': 90,
      掲載: '○',
      予約可: '○',
      有効: '○',
    },
    {
      ID: 'normal_3',
      メニュー名: 'フェイシャルエステ 45分',
      説明: 'お肌の状態に合わせたカスタマイズフェイシャル',
      カテゴリ: 'エステ',
      検索用カテゴリ: 'エステ',
      価格: 5500,
      価格表示: '〜表示',
      '所要時間(分)': 45,
      掲載: '○',
      予約可: '○',
      有効: '○',
    },
    {
      ID: 'normal_4',
      メニュー名: 'カスタマイズコース',
      説明: 'お客様のご要望に合わせてメニューを組み合わせます',
      カテゴリ: 'その他',
      検索用カテゴリ: 'その他',
      価格: 0,
      価格表示: '要問い合わせ',
      '所要時間(分)': 120,
      掲載: '○',
      予約可: '×',
      有効: '○',
    },
    {
      ID: 'normal_5',
      メニュー名: '【期間限定】リフレクソロジー 30分',
      説明: '足裏の反射区を刺激して全身の疲れを癒します',
      カテゴリ: 'リラクゼーション',
      検索用カテゴリ: 'リラクゼーション',
      価格: 3300,
      価格表示: '通常',
      '所要時間(分)': 30,
      掲載: '×',
      予約可: '○',
      有効: '×',
      削除: '○',
    },
    {
      ID: '',
      メニュー名: '【カテゴリ選択肢】',
      説明: 'リラクゼーション、ボディケア・マッサージ、ヘッドスパ、エステ、その他',
      カテゴリ: 'その他',
      検索用カテゴリ: 'その他',
      価格: 0,
      価格表示: '【価格表示選択肢】通常、〜表示、要問い合わせ',
      '所要時間(分)': 0,
      掲載: '【○か×】',
      予約可: '【○か×】',
      有効: '【○か×】',
      削除: '【○で削除】',
    }
  ];
};

export const getCouponMenuTemplate = (): CouponMenuCSV[] => {
  return [
    {
      ID: '',
      種別: '全員',
      クーポン名: '【入力ルール】40文字以内で入力',
      内容: '【入力ルール】90文字以内で入力。クーポンの詳細内容と特典を記載',
      カテゴリ: 'リラクゼーション',
      検索用カテゴリ: 'リラクゼーション',
      価格: 0,
      '所要時間(分)': 0,
      提示条件: '予約時',
      利用条件: '【20文字以内】',
      有効期限: '2025-12-31',
      自動更新: '×',
      メニュー指定: 'あり',
      掲載: '○',
      有効: '○',
    },
    {
      ID: 'coupon_1',
      種別: '新規',
      クーポン名: '【初回限定】ドライヘッドスパ体験',
      内容: '初めての方限定の特別価格でご提供。極上の癒しをお試しください',
      カテゴリ: 'ヘッドスパ',
      検索用カテゴリ: 'ヘッドスパ',
      価格: 4400,
      '所要時間(分)': 60,
      提示条件: '予約時',
      利用条件: '初回のお客様限定',
      有効期限: '2025-09-30',
      自動更新: '×',
      メニュー指定: 'あり',
      掲載: '○',
      有効: '○',
    },
    {
      ID: 'coupon_2',
      種別: '全員',
      クーポン名: '秋支度♪【弱った胃腸に◎】ドライヘッドスパ&ホット腸セラピー',
      内容: '期間限定☆ 腸を整えると心も整う！寝落ち必至ヘッドスパ&心和らぐ香りのオイルの腸もみで自律神経ケア',
      カテゴリ: 'ボディケア・マッサージ',
      検索用カテゴリ: 'ボディケア・マッサージ',
      価格: 7480,
      '所要時間(分)': 85,
      提示条件: '予約時',
      利用条件: 'どなたでもご利用可',
      有効期限: '2025-09-14',
      自動更新: '○',
      メニュー指定: 'あり',
      掲載: '○',
      有効: '○',
    },
    {
      ID: 'coupon_3',
      種別: '再来',
      クーポン名: '【リピーター様限定】20%OFFクーポン',
      内容: '2回目以降のご来店のお客様限定。全メニュー20%OFF',
      カテゴリ: 'その他',
      検索用カテゴリ: 'その他',
      価格: 0,
      '所要時間(分)': 0,
      提示条件: '予約時＆入店時',
      利用条件: '2回目以降のお客様',
      有効期限: '2025-10-31',
      自動更新: '×',
      メニュー指定: 'なし',
      掲載: '○',
      有効: '○',
    },
    {
      ID: 'coupon_4',
      種別: '新規',
      クーポン名: '【平日限定】お試しフェイシャル',
      内容: '平日14時〜17時限定。初回の方のみご利用いただけます',
      カテゴリ: 'エステ',
      検索用カテゴリ: 'エステ',
      価格: 3300,
      '所要時間(分)': 30,
      提示条件: '入店時',
      利用条件: '平日14-17時限定',
      有効期限: '2025-08-31',
      自動更新: '○',
      メニュー指定: 'あり',
      掲載: '×',
      有効: '×',
      削除: '○',
    },
    {
      ID: '',
      種別: '【選択肢】',
      クーポン名: '【種別】全員、新規、再来',
      内容: '【提示条件】予約時、入店時、会計時、予約時＆入店時',
      カテゴリ: 'その他',
      検索用カテゴリ: 'その他',
      価格: 0,
      '所要時間(分)': 0,
      提示条件: '【選択肢参照】',
      利用条件: '【20文字以内】',
      有効期限: 'YYYY-MM-DD形式',
      自動更新: '【○か×】',
      メニュー指定: '【あり、なし】',
      掲載: '【○か×】',
      有効: '【○か×】',
      削除: '【○で削除】',
    }
  ];
};